import React, { createContext, PropsWithChildren, useCallback } from 'react';

import { ratesService } from '@shared/services';
import { StorageKey } from '@shared/storage';

import { useLocalStorage } from 'react-use';
import { useSwyftxMarginStore } from 'src/lib/margin';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

import { SwyftxPlatform } from '../utils.types';

type UIContextType = {
  currentPlatform?: SwyftxPlatform;
  openOnHover?: boolean;
  setCurrentPlatform: (newCurrentPlatform?: SwyftxPlatform) => void;
  setOpenOnHover: (openOnHover: boolean) => void;
};

const UIContext = createContext<UIContextType>({
  currentPlatform: 'swyftx',
  openOnHover: true,
  setCurrentPlatform: () => undefined,
  setOpenOnHover: () => undefined,
});

const DEFAULT_CURRENCY = 'BTC';

const UIContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [currentPlatform, setPlatform] = useLocalStorage<SwyftxPlatform>(StorageKey.CURRENT_PLATFORM, 'swyftx');
  const [openOnHover, setOpenOnHover] = useLocalStorage(StorageKey.NAVIGATION_HOVER_OPEN, true);
  const { setTradeCurrency } = useSwyftxMarginStore();

  const { navigate } = useNavigateRoute();

  const setCurrentPlatform = useCallback(
    (newCurrentPlatform?: SwyftxPlatform) => {
      setPlatform(newCurrentPlatform);
      setTradeCurrency(DEFAULT_CURRENCY);

      switch (newCurrentPlatform) {
        case 'swyftx':
        case undefined:
          // In the case of Swyftx (Spot) we don't need to redirect as the router will handle the route rendering which will naturally cause a redirect to /dashboard
          break;
        case 'swyftx-pro':
          navigate(NavigationURLs.SwyftxPro, { pathParams: { asset: DEFAULT_CURRENCY } });
          break;
        case 'derivatives':
          navigate(NavigationURLs.MarginAccount);
          break;
        default:
          newCurrentPlatform satisfies never;
      }

      window.setTimeout(() => ratesService.forceUpdate(), 100);
    },
    [navigate, setPlatform, setTradeCurrency],
  );

  return (
    <UIContext.Provider value={{ currentPlatform, openOnHover, setOpenOnHover, setCurrentPlatform }}>
      {children}
    </UIContext.Provider>
  );
};

export { UIContextProvider, UIContext };
